import React from 'react';
import PropTypes from 'prop-types';
import { Field } from 'redux-form';
import Select from 'react-select';
import attributes, { PROPERTY_TYPE_ATTR_VAL } from '../../../constants/attributes';
import Commons from '../../../helpers/Commons';
import { allPropertyTypes } from '../../../constants/propertyTypes';
import DropdownIndicator from '../../common/SelectComponent';
import style from '../../../constants/dropdownStyle';
import LabelAndField from './LabelAndField';
import { isAU } from '../../../constants/crux';

export const getAttribute = (props, key) => {
    const attribute = parseInt(Commons.get(props, `property.attributes.${key}`));
    return attribute || undefined;
};

export const getPropertyType = ({ property }) => {
    const propertyType = Commons.get(property, 'attributes.propertyType') || Commons.get(property, 'propertyType');
    if (propertyType && propertyType !== 'OTHER') {
        return {
            label: Commons.capitalizeFirstLetter(propertyType).toString(),
            value: propertyType.toLowerCase(),
        };
    }
    return { label: '', value: '' };
};

const getCustomStyles = () => ({
    indicatorSeparator: styles => ({
        ...styles,
        display: 'none',
    }),
    control: (styles, {
        isFocused,
    }) => ({
        ...styles,
        marginTop: '9px',
        height: '37px',
        width: '115px',
        borderRadius: '2px',
        boxShadow: 'none',
        borderColor: isFocused ?
            style.normalStyle.borderColorOnFocus :
            style.normalStyle.borderColor,
        backgroundColor: isFocused ?
            style.normalStyle.backgroundColorOnFocus :
            style.normalStyle.backgroundColor,
        '&:hover': {
            borderColor: style.normalStyle.borderColorOnHover,
        },
    }),
    menu: styles => ({
        ...styles,
        width: '115px',
    }),
    menuList: styles => ({
        ...styles,
        width: '115px',
        maxHeight: '200px',
        minHeight: '45px',
    }),
});

export const renderSelect = ({
     input, customOptions, id, customStyles, label,
 }) => (
    <Select
        aria-label={label}
        name={id}
        options={customOptions}
        value={input.value}
        onChange={input.onChange}
        components={{ DropdownIndicator }}
        isOptionDisabled={option => option.disabled}
        styles={customStyles || getCustomStyles()}
        isSearchable={false}
    />
);

const isLandOrFloorArea = attribute => attribute.value === attributes[3].value ||
        attribute.value === attributes[4].value;

const getMaxLength = (attribute) => {
    const { value } = attribute;
    switch (value) {
        case 'landArea':
        case 'floorArea':
            return 6;
        case 'carSpaces':
            return 3;
        default:
            return 2;
    }
};

const displayError = field => attributes.find(o => o.value === field);

const UpdateAttributesFields = ({
    errors,
    setFocusedField,
    setErrors,
    focusedField = {},
    excludeFields = [],
    initialValues,
}) => {
    const customOptions = [];
    const isPropertyTypeExcluded = excludeFields.includes(PROPERTY_TYPE_ATTR_VAL);

    if (!isPropertyTypeExcluded) {
        allPropertyTypes.forEach((propertyType) => {
            customOptions.push({ label: propertyType, value: propertyType.toLowerCase() });
        });
    }

    const errorMsg = focusedField.label && errors[focusedField.field] ? `${focusedField.label}: ${errors[focusedField.field]}` : '';

    return (
        <React.Fragment>
            <div className="update-attributes-container" style={{ gap: isAU ? '27px' : '34px' }}>
                {
                    !isPropertyTypeExcluded &&
                    (
                        <div className="attribute-container">
                            <span className="attribute-label">Property Type</span>
                            <Field
                                label="propertyType"
                                name="propertyType"
                                id="propertyType"
                                key="propertyType"
                                component={renderSelect}
                                customOptions={customOptions}
                            />
                        </div>
                    )
                }
                {attributes.map((attribute) => {
                    if (!excludeFields.includes(attribute.value)) {
                        const hasUnit = isLandOrFloorArea(attribute);
                        const maxLength = getMaxLength(attribute);

                        return (
                            <LabelAndField
                                key={attribute.value}
                                label={attribute.label}
                                field={attribute.value}
                                maxLength={maxLength}
                                hasUnit={hasUnit}
                                errors={errors}
                                setFocusedField={setFocusedField}
                                setErrors={setErrors}
                                hasEditableUnits={attribute?.hasEditableUnits}
                                initialValues={initialValues}
                            />
                        );
                    }
                    return null;
                })}

            </div>
            <p className="text-danger mb-0 error-message">{displayError(focusedField.field) && errorMsg}</p>
        </React.Fragment>
    );
};

renderSelect.propTypes = {
    id: PropTypes.string,
    input: PropTypes.object,
    customOptions: PropTypes.array,
    customStyles: PropTypes.object,
};

UpdateAttributesFields.propTypes = {
    errors: PropTypes.object,
    setFocusedField: PropTypes.func,
    setErrors: PropTypes.func,
    focusedField: PropTypes.object,
    excludeFields: PropTypes.arrayOf(PropTypes.string),
};

export default UpdateAttributesFields;
